import { Component, inject, input, OnInit, signal } from '@angular/core';
import { DokaButton, DokaCheckbox, DokaModal, DokaModalController } from '@doka/angular-core';
import { I18NextEagerPipe, I18NextService } from 'angular-i18next';
import { firstValueFrom } from 'rxjs';
import {
  CurrenciesClient,
  ICountryModel,
  ICurrencyModel,
  SetCurrencyCountriesModel,
} from 'src/api/services/generated/api-clients';
import { ToastService } from 'src/app/shared/services/toast.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';

@Component({
  templateUrl: './edit-currency-countries-slide-over.component.html',
  imports: [DokaButton, DokaCheckbox, DokaModal, I18NextEagerPipe],
})
export class EditCurrencyCountriesSlideOverComponent implements OnInit {
  private readonly currenciesClient = inject(CurrenciesClient);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly toastService = inject(ToastService);
  private readonly userInfoService = inject(UserInfoService);
  private readonly i18n = inject(I18NextService);

  public readonly currency = input.required<ICurrencyModel>();
  public readonly countries = input.required<ICountryModel[]>();

  protected readonly isLoading = signal(false);
  protected readonly selectedCountryIds = signal(new Set<string>());

  public ngOnInit() {
    this.selectedCountryIds.set(
      new Set(
        this.countries()
          .filter((country) => country.isoCurrencyCodes.includes(this.currency().isoCurrencyCode))
          .map((country) => country.id),
      ),
    );
  }

  protected closeModal() {
    this.modalCtrl.dismiss();
  }

  protected isSelected(countryId: string): boolean {
    return this.selectedCountryIds().has(countryId);
  }

  protected isMainCurrency(country: ICountryModel): boolean {
    return country.mainIsoCurrencyCode === this.currency().isoCurrencyCode;
  }

  protected canEdit(country: ICountryModel): boolean {
    return this.userInfoService.isSuperAdmin() || this.userInfoService.regionIds().includes(country.regionId);
  }

  protected toggleCountry(country: ICountryModel, selected: boolean) {
    if (!this.canEdit(country) || this.isMainCurrency(country)) {
      return;
    }

    const selection = new Set(this.selectedCountryIds());
    if (selected) {
      selection.add(country.id);
    } else {
      selection.delete(country.id);
    }
    this.selectedCountryIds.set(selection);
  }

  protected countryLabel(country: ICountryModel): string {
    const mainCurrencySuffix = this.isMainCurrency(country)
      ? ` (${this.i18n.t('MasterData.Currencies.MainCurrencySuffix')})`
      : '';
    return `${country.countryName} (${country.countryCode})${mainCurrencySuffix}`;
  }

  protected async submit() {
    this.isLoading.set(true);
    try {
      await firstValueFrom(
        this.currenciesClient.setCurrencyCountries(
          this.currency().isoCurrencyCode,
          new SetCurrencyCountriesModel({ countryIds: [...this.selectedCountryIds()] }),
        ),
      );
      this.modalCtrl.dismiss(true, 'saved');
    } catch {
      await this.toastService.showError(this.i18n.t('MasterData.Currencies.AssignmentError'));
    } finally {
      this.isLoading.set(false);
    }
  }
}
